import { Monitor, Moon, Sun, type LucideIcon } from 'lucide-react'
import { useTheme, type Theme } from '@/hooks/use-theme'
import { useSlidingIndicator } from '@/hooks/use-sliding-indicator'
import { cn } from '@/lib/utils'

/**
 * ThemeSegmentedControl — 3-option theme control (Claro / Escuro / Auto),
 * Story 10.1 (replaces the old binary `ThemeToggle`, AC-15).
 *
 * A `radiogroup` of three segments wired to `useTheme`. The same component is
 * used in two places, so it stays consistent (AC-9): both consume the same hook,
 * so changing the theme in the sidebar popover updates the `/settings` control
 * and vice-versa.
 *
 * `compact` (popover) renders icon-only segments with `aria-label`/`title`;
 * the default (settings) shows the icon + the Portuguese label.
 *
 * Tokens mirror the approved mockup's `.segmented` rule: a pill track in
 * `bg-deep` with the active segment lifted onto `bg-panel-solid`.
 */

interface ThemeOption {
  value: Theme
  label: string
  icon: LucideIcon
}

const THEME_OPTIONS: ThemeOption[] = [
  { value: 'light', label: 'Claro', icon: Sun },
  { value: 'dark', label: 'Escuro', icon: Moon },
  { value: 'auto', label: 'Auto', icon: Monitor },
]

export interface ThemeSegmentedControlProps {
  /** Icon-only segments (sidebar popover). Default shows labels (settings). */
  compact?: boolean
  className?: string
}

export function ThemeSegmentedControl({ compact = false, className }: ThemeSegmentedControlProps) {
  const { theme, setTheme } = useTheme()

  // Mesmo mecanismo do `Seg` e das abas: o estado ativo é UM elemento que se
  // desloca. Este controlo não usa o `Seg` porque é genuinamente outra coisa —
  // ícones, modo compacto, e semântica de rádio (escolhas mutuamente
  // exclusivas de uma definição, não interruptores de filtro). O que se
  // partilha é a medição, não a marcação.
  const { containerRef, box } = useSlidingIndicator<HTMLDivElement>(
    '[aria-checked="true"]',
    `${theme}|${compact ? 'compact' : 'full'}`,
  )

  return (
    <div
      ref={containerRef}
      role="radiogroup"
      aria-label="Tema"
      className={cn(
        'border-hair bg-deep relative inline-flex items-center gap-0.5 rounded-full border p-0.5',
        compact && 'flex w-full',
        className,
      )}
    >
      {box ? (
        <span
          aria-hidden="true"
          className={cn(
            'bg-panel-solid pointer-events-none absolute rounded-full shadow-sm',
            'transition-[transform,width] duration-200 [transition-timing-function:var(--ease-out)]',
          )}
          style={{
            transform: `translate(${box.left}px, ${box.top}px)`,
            width: `${box.width}px`,
            height: `${box.height}px`,
          }}
        />
      ) : null}
      {THEME_OPTIONS.map((opt) => {
        const Icon = opt.icon
        const active = theme === opt.value
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={opt.label}
            title={opt.label}
            onClick={() => setTheme(opt.value)}
            className={cn(
              'relative z-[1] inline-flex items-center justify-center gap-1.5 rounded-full text-xs font-medium',
              'transition-[background,color,box-shadow] duration-150 ease-out',
              compact ? 'flex-1 px-0 py-1.5' : 'px-3 py-1.5',
              active ? 'text-ink font-semibold' : 'text-ink-2 hover:text-ink',
              // Sem indicador (antes da primeira medição, ou num contentor sem
              // layout) o material volta para o próprio botão — o tema
              // escolhido nunca fica sem marca.
              active && !box && 'bg-panel-solid shadow-sm',
            )}
          >
            <Icon className="size-3.5 shrink-0" aria-hidden="true" />
            {compact ? null : <span>{opt.label}</span>}
          </button>
        )
      })}
    </div>
  )
}

/**
 * @deprecated Story 10.1 (AC-15) retired the binary toggle. The named export is
 * kept as an alias to {@link ThemeSegmentedControl} so any stray import resolves;
 * new code should import `ThemeSegmentedControl` directly. The sidebar footer no
 * longer renders a standalone toggle — theme switching lives in the user-card
 * popover and on `/settings`.
 */
export const ThemeToggle = ThemeSegmentedControl
